import { useEffect, useMemo, useState } from "react";
import { Grid, Tab, Tabs } from "@mui/material";

import { TabPanel } from "../../components/tab-panel";
import { planReviewTaskColumns } from "./grid-columns";
import { fileProcessingColumns } from "./file-processign/grid-columns";
import { privateProviderColumns } from "./private-provider/grid-columns";
import { EnergyModelingTab } from "./energy-modeling/energy-modeling-tab.component";
import { FileProcessingTab } from "./file-processign/file-processing-tab.component";
import { DesignComponentTab } from "./design-analysis/design-analysis-tab.component";
import { PrivateProviderTab } from "./private-provider/private-provider-tab.component";
import { ReportProcessingTab } from "./report-processing/report-processing-tab.component";
import { RebateProcessingTab } from "./rebate-processing/rebate-processing-tsb.component";
import { rebateProcessingColumns } from "./rebate-processing/grid-columns";
import { reportProcessingColumns } from "./report-processing/grid-columns";

interface PlanReviewTabs {
    permissionForTab: any;
    userRole: any;
    isRoleLoading: boolean;
    handleUserRole: () => void;
}

export const PlanReviewTabs = ({ permissionForTab, userRole, isRoleLoading, handleUserRole }: PlanReviewTabs) => {

    const [value, setValue] = useState('energy-modeling');

    const moduleCode = {
        ENERGY_MODELING: { code: "PDB_ENERGY", title: "Energy Modeling" },
        PRIVATE_PROVIDER_PLAN_REVIEW: { code: "PDB_PPI", title: "Private Provider Plan Review" },
        HVAC_DESIGN_ANALYSIS: { code: "PDB_HVAC", title: "HVAC Design Analysis" },
        FILE_PROCESSING: { code: "PDB_FILE", title: "File Processing" },
        REPORT_PROCESSING: { code: "PDB_REPORT", title: "Report Processing" },
        REBATE_PROCESSING: { code: "PDB_REBATE", title: "Rebate Processing" },
    }

    const availableTabs = useMemo(
        () =>
            [
                permissionForTab?.eneryModelingTab && 'energy-modeling',
                permissionForTab?.hvacDesignTab && 'design-analysis',
                permissionForTab?.fileProcessingTab && 'file-processing',
                permissionForTab?.reportProcessingTab && 'report-processing',
                permissionForTab?.rebateProcessingTab && 'rebate-processing',
                permissionForTab?.privateProviderTab && 'private-provider-plan-review',
            ].filter(Boolean),
        [permissionForTab]
    );

    useEffect(() => {
        if (!availableTabs.includes(value)) {
            setValue(availableTabs[0] as string);
        }
    }, [availableTabs]);

    const handleTabsChange = (event: any, value: any) => { setValue(value); };

    return (
        <Grid item>
            <Tabs value={value} onChange={handleTabsChange}>
                <Tab value="energy-modeling" label="ENERGY MODELING" hidden={!permissionForTab?.eneryModelingTab} />
                <Tab value="design-analysis" label="HVAC DESIGN ANALYSIS" hidden={!permissionForTab?.hvacDesignTab} />
                <Tab value="file-processing" label="FILE PROCESSING" hidden={!permissionForTab?.fileProcessingTab} />
                <Tab value="report-processing" label="REPORT PROCESSING" hidden={!permissionForTab?.reportProcessingTab} />
                <Tab value="rebate-processing" label="REBATE PROCESSING" hidden={!permissionForTab?.rebateProcessingTab} />
                <Tab value="private-provider-plan-review" label="PRIVATE PROVIDER PLAN REVIEW" hidden={!permissionForTab?.privateProviderTab} />
            </Tabs>

            {/* Conditionally render TabPanels based on selected tab and permissions */}
            <TabPanel selectedValue={value} value="energy-modeling">
                <EnergyModelingTab
                    title={moduleCode.ENERGY_MODELING.title}
                    moduleCode={moduleCode.ENERGY_MODELING.code}
                    dashboardColumns={planReviewTaskColumns}
                    userRole={userRole}
                    isRoleLoading={isRoleLoading}
                    handleUserRole={handleUserRole} />
            </TabPanel>
            <TabPanel selectedValue={value} value="private-provider-plan-review">
                <PrivateProviderTab
                    title={moduleCode.PRIVATE_PROVIDER_PLAN_REVIEW.title}
                    moduleCode={moduleCode.PRIVATE_PROVIDER_PLAN_REVIEW.code}
                    dashboardColumns={privateProviderColumns}
                    userRole={userRole}
                    isRoleLoading={isRoleLoading}
                    handleUserRole={handleUserRole} />
            </TabPanel>
            <TabPanel selectedValue={value} value="design-analysis">
                <DesignComponentTab
                    title={moduleCode.HVAC_DESIGN_ANALYSIS.title}
                    moduleCode={moduleCode.HVAC_DESIGN_ANALYSIS.code}
                    dashboardColumns={planReviewTaskColumns}
                    userRole={userRole}
                    isRoleLoading={isRoleLoading}
                    handleUserRole={handleUserRole} />
            </TabPanel>
            <TabPanel selectedValue={value} value="file-processing">
                <FileProcessingTab
                    title={moduleCode.FILE_PROCESSING.title}
                    moduleCode={moduleCode.FILE_PROCESSING.code}
                    dashboardColumns={fileProcessingColumns}
                    userRole={userRole}
                    isRoleLoading={isRoleLoading}
                    handleUserRole={handleUserRole} />
            </TabPanel>
            <TabPanel selectedValue={value} value="report-processing">
                <ReportProcessingTab
                    title={moduleCode.REPORT_PROCESSING.title}
                    moduleCode={moduleCode.REPORT_PROCESSING.code}
                    dashboardColumns={reportProcessingColumns}
                    userRole={userRole}
                    isRoleLoading={isRoleLoading}
                    handleUserRole={handleUserRole} />
            </TabPanel>
            <TabPanel selectedValue={value} value="rebate-processing">
                <RebateProcessingTab
                    title={moduleCode.REBATE_PROCESSING.title}
                    moduleCode={moduleCode.REBATE_PROCESSING.code}
                    dashboardColumns={rebateProcessingColumns}
                    userRole={userRole}
                    isRoleLoading={isRoleLoading}
                    handleUserRole={handleUserRole} />
            </TabPanel>
        </Grid>
    );
}